// SP-2 WS-7 — plain-language copy for the Monthly Cycle Cockpit. Every
// screen states the next action in one sentence composed from DTO facts; no
// raw enum strings reach the user (STATEMENT_STATUS_LABEL + these maps are
// the vocabulary). Pure functions — date formatting is injected so the
// module stays testable without the auth/timezone context.

import {
  STATEMENT_STATUS_LABEL,
  type MonthlyCycleDto,
  type MonthlyCycleStepDto,
  type MonthlyCycleStepKey,
} from "@/api/portfolioAdmin/loanStatementsApi";
import { formatCalendarDate } from "@/design-system/lib/format";
import { fmtMoney } from "../paymentTracking/servicingFormat";

export const CYCLE_STEP_LABEL: Record<MonthlyCycleStepKey, string> = {
  statement: "Statement generated",
  "phoenix-review": "Phoenix review & approve",
  "tps-review": "Servicer review & post",
  "borrower-pays": "Borrower pays",
  "payment-recorded": "Payment recorded",
  payout: "Payout distributed",
  complete: "Cycle complete",
};

/** "2026-07" → "July 2026". Falls back to the raw string on a bad shape. */
export function monthLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return d.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** The one step the card narrates: the first blocked step, else the first
 *  active one (a blocked step is always the thing to fix first). */
export function currentStep(cycle: MonthlyCycleDto): MonthlyCycleStepDto | null {
  return (
    cycle.steps.find((s) => s.state === "blocked") ??
    cycle.steps.find((s) => s.state === "active") ??
    null
  );
}

export type CycleActionKind =
  | "generate"
  | "regenerate"
  | "send"
  | "record"
  | "reverse";

export interface CycleAction {
  kind: CycleActionKind;
  label: string;
  /** The permission that gates the button (ADMIN always passes). */
  permission: string;
}

/** The single next action for the current step — null when the baton is
 *  external (servicer / borrower) or the cycle is complete. */
export function nextAction(
  cycle: MonthlyCycleDto,
  step: MonthlyCycleStepDto,
): CycleAction | null {
  switch (step.key) {
    case "statement":
      if (step.state === "blocked" && cycle.statement?.status === "Returned") {
        return {
          kind: "regenerate",
          label: "Fix & regenerate",
          permission: "Statements.Generate",
        };
      }
      if (step.state === "active") {
        return {
          kind: "generate",
          label: "Generate statement",
          permission: "Statements.Generate",
        };
      }
      return null;
    case "phoenix-review":
      // The D-9 gate: one action opens the review screen, where B3 inspects
      // the lines, adjusts with a reason if needed, approves, and sends.
      if (step.state === "active" && cycle.statement?.status === "PendingReview") {
        return {
          kind: "send",
          label: "Review & send to servicer",
          permission: "Statements.Generate",
        };
      }
      return null;
    case "payment-recorded":
      if (step.state === "blocked" && cycle.reversalPrefill) {
        return {
          kind: "reverse",
          label: "Reverse recorded payment",
          permission: "Payments.Reverse",
        };
      }
      if (step.state === "active" && cycle.recordPrefill) {
        return {
          kind: "record",
          label: "Record payment",
          permission: "Payments.Create",
        };
      }
      return null;
    default:
      return null;
  }
}

type DateFmt = (iso: string | null | undefined) => string;

/** One plain sentence describing where the cycle stands and who holds the
 *  baton. `date` is the caller's timezone-bound formatter. */
export function statusSentence(
  cycle: MonthlyCycleDto,
  step: MonthlyCycleStepDto,
  date: DateFmt,
): string {
  const month = monthLabel(cycle.period);
  switch (step.key) {
    case "statement":
      if (step.state === "blocked" && cycle.statement?.status === "Returned") {
        const reason = cycle.statement.returnedReason;
        return reason
          ? `The servicer returned the ${month} statement: “${reason}”. Correct the underlying data, then regenerate it.`
          : `The servicer returned the ${month} statement for correction. Correct the underlying data, then regenerate it.`;
      }
      return `No statement yet for ${month}. Statements generate automatically on the 1st — or generate one now.`;
    case "phoenix-review": {
      const status = cycle.statement?.status;
      if (status === "PendingReview") {
        return `The ${month} statement is waiting for your review — check the numbers, approve, and send it to the servicer.`;
      }
      return `The ${month} statement is in Phoenix review before it goes to the servicer.`;
    }
    case "tps-review": {
      const status = cycle.statement?.status;
      if (status === "TpsApprovalPending") {
        return "Waiting on the servicer — they review and post the bill to the borrower.";
      }
      return status
        ? `The ${month} statement is ${STATEMENT_STATUS_LABEL[status].toLowerCase()}.`
        : `The ${month} statement is with the servicer.`;
    }
    case "borrower-pays": {
      if (step.state === "blocked" && cycle.payment?.status === "Returned") {
        const reason = cycle.payment.failureReason;
        return reason
          ? `The borrower's payment was returned by the bank — ${reason}.`
          : "The borrower's payment was returned by the bank.";
      }
      const posted = cycle.statement?.postedAt
        ? ` — statement posted ${date(cycle.statement.postedAt)}`
        : "";
      // Due date is a wall-calendar date (UTC-midnight) — render its
      // calendar day, never the injected timezone-converting formatter
      // (which rolls a UTC-midnight value back a day in Phoenix).
      const due = cycle.dueDate ? `, due ${formatCalendarDate(cycle.dueDate)}` : "";
      return `Waiting on the borrower${posted}${due}. Bank transfers take 3–5 business days to clear.`;
    }
    case "payment-recorded": {
      if (step.state === "blocked") {
        return "A returned payment is still recorded on the books — reverse it to keep the ledger honest.";
      }
      const amount = cycle.recordPrefill?.amount ?? cycle.payment?.amount;
      const cleared = cycle.payment?.clearedAt;
      return `The borrower's payment${amount != null ? ` of ${fmtMoney(amount)}` : ""} cleared${cleared ? ` on ${date(cleared)}` : ""}. Record it so the waterfall can split the money.`;
    }
    case "payout":
      if (step.state === "blocked" && cycle.distribution?.blockReason) {
        return `The payout is blocked: ${cycle.distribution.blockReason}.`;
      }
      return "Waiting on the servicer — they approve the payout that distributes the money.";
    case "complete": {
      const amount =
        cycle.servicingPayment?.amount ?? cycle.distribution?.totalAmount ?? null;
      return amount != null
        ? `This cycle is complete. ${fmtMoney(amount)} was received and distributed.`
        : "This cycle is complete.";
    }
  }
}
